import Image from "next/image";

import cn from "classnames";

import { ArrowRightIcon } from "@/components/icons/arrowRight.icon";
import { Link } from "@/components/link/link.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { resolveLinkHref } from "@/utils/url.util";

import { brown } from "@/app/font";
import type { GetHomePageQueryResult } from "@/sanity.types";

export const OfferCard = ({ block }: { block: OfferCardBlock }) => {
  if (!block?.headline) return null;

  const headingId = `offer-card-title-${block._key}`;
  const ctaHref = resolveLinkHref(block.ctaLink);
  const showCta = Boolean(block.ctaLabel && ctaHref && ctaHref !== "#");
  const desktopImage = block.image?.url || block.mobileImage?.url;
  const mobileImage = block.mobileImage?.url || block.image?.url;

  return (
    <div
      data-testid="offer-card"
      aria-labelledby={headingId}
      className="bg-primary-100 flex h-full min-h-full flex-1 flex-col justify-between gap-8 rounded-3xl px-6 py-8 lg:px-10 lg:py-10">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-start gap-8 lg:flex-row lg:justify-between lg:gap-10">
          <section className="flex flex-col items-start gap-5 max-lg:order-2 md:w-2/3 xl:w-1/2">
            {block.badge ? (
              <div
                data-testid="offer-card-badge"
                className="bg-success inline-block rounded px-3 py-1.5">
                <Text
                  variant="preHeaderSm"
                  component="p"
                  className="tracking-[0.04em] text-white uppercase">
                  {block.badge}
                </Text>
              </div>
            ) : null}
            {block.preHeader ? (
              <Text
                variant="preHeaderSm"
                component="p"
                className="text-adt-blue tracking-[0.06em] uppercase">
                {block.preHeader}
              </Text>
            ) : null}
            <div id={headingId} className="flex flex-col gap-1">
              <Text variant="h3" component="h2" className="text-success">
                {block.headline}
              </Text>
              {block.subheadline ? (
                <Text
                  variant="h5"
                  component="p"
                  className="text-greyscale-headline text-2xl">
                  {block.subheadline}
                </Text>
              ) : null}
            </div>
            {block.description ? (
              <Text
                variant="body1"
                component="p"
                className="text-greyscale-body">
                {block.description}
              </Text>
            ) : null}
          </section>

          {mobileImage || desktopImage ? (
            <section className="relative mx-auto h-64 w-full max-w-55 max-lg:order-1 md:w-1/3 xl:w-1/2 xl:max-w-none">
              {mobileImage ? (
                <Image
                  src={mobileImage}
                  alt={block.mobileImage?.alt || block.image?.alt || ""}
                  fill
                  sizes="220px"
                  className="object-contain lg:hidden"
                />
              ) : null}
              {desktopImage ? (
                <Image
                  src={desktopImage}
                  alt={block.image?.alt || ""}
                  fill
                  sizes="240px"
                  className="hidden object-contain lg:block"
                />
              ) : null}
            </section>
          ) : null}
        </div>
        {showCta ? (
          <Link
            href={ctaHref}
            target={block.ctaLink?.openInNewTab ? "_blank" : undefined}
            rel={
              block.ctaLink?.openInNewTab ? "noopener noreferrer" : undefined
            }
            className={cn(
              brown.className,
              "text-secondary inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F6B221] px-8 py-3.5 text-base font-bold hover:bg-[#E5A40F] lg:w-max lg:px-10",
            )}>
            {block.ctaLabel}
            <span aria-hidden>
              <ArrowRightIcon width={18} height={18} title="" />
            </span>
          </Link>
        ) : null}
      </div>
      {block.legal?.length ? (
        <div data-testid="offer-card-legal">
          <RichText
            blockComponents={{
              normal: ({ children }) => (
                <Text
                  variant="legal"
                  component="p"
                  className="text-greyscale-body">
                  {children}
                </Text>
              ),
            }}>
            {block.legal}
          </RichText>
        </div>
      ) : null}
    </div>
  );
};

type PageBuilderBlock = NonNullable<
  NonNullable<GetHomePageQueryResult>["pageBuilder"]
>[number];

export type OfferCardBlock = Extract<PageBuilderBlock, { _type: "offerCard" }>;
